import { useCallback, useRef } from "react";

import { authTokenServiceTokenPostV2 } from "@btnet/services-btnet/handlers/auth-token/auth-token";

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry

/**
 * Hook to manage JWT auth tokens for external BT services.
 * Lazily fetches a token on first use and caches it in memory.
 * Auto-refreshes when the token is within 5 minutes of expiry.
 *
 * Usage:
 *   const { getToken } = useAuthToken("https://search.buildertrend.net/");
 *   const token = await getToken(); // cached after first call
 */
export function useAuthToken(audience: string) {
    const tokenRef = useRef<string | null>(null);
    const expiresAtRef = useRef<number>(0);
    const fetchPromiseRef = useRef<Promise<string> | null>(null);

    const fetchToken = useCallback(async (): Promise<string> => {
        const response = await authTokenServiceTokenPostV2({ audience });

        // apixHandler returns an AxiosResponse — data is in response.data
        const data = response.data;
        const accessToken = data.access_token;
        if (!accessToken) {
            throw new Error("Auth token response missing access_token");
        }

        tokenRef.current = accessToken;
        expiresAtRef.current = Date.now() + (data.expires_in ?? 0) * 1000 - REFRESH_BUFFER_MS;
        fetchPromiseRef.current = null;

        return accessToken;
    }, [audience]);

    const getToken = useCallback(async (): Promise<string> => {
        // Return cached token if still valid
        if (tokenRef.current && Date.now() < expiresAtRef.current) {
            return tokenRef.current;
        }

        // Deduplicate concurrent requests — if a fetch is already in flight, await it
        if (fetchPromiseRef.current) {
            return fetchPromiseRef.current;
        }

        const promise = fetchToken();
        fetchPromiseRef.current = promise;
        return promise;
    }, [fetchToken]);

    const clearToken = useCallback(() => {
        tokenRef.current = null;
        expiresAtRef.current = 0;
        fetchPromiseRef.current = null;
    }, []);

    return { getToken, clearToken };
}
